import { type Dispatch, type SetStateAction, useEffect, useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover.tsx";
import { UseData } from "@/components/widgets/Backtest/services/UseData.ts";
import type { IBacktestConfig } from "@/components/widgets/Backtest/types.ts";
import { useGetUserDataFromToken } from "@/hooks/useGetUserDataFromToken";
import { KEY } from "@/shared/constants/keyboard.ts";

export const BacktestSettingsSelector = ({
	selectedBacktest,
	setSelectedBacktest,
}: {
	selectedBacktest?: IBacktestConfig;
	setSelectedBacktest: Dispatch<SetStateAction<IBacktestConfig | undefined>>;
}) => {
	const userData = useGetUserDataFromToken();
	const userId = userData?.userId.split("user_id_")[1] || "";

	const [screenerConditions, setScreenerConditions] = useState<
		IBacktestConfig[]
	>([]);

	const { execute, isLoading } = UseData.create().useGetConditionData();

	useEffect(() => {
		const getData = async (userId: string) => {
			const params = { fields: ["_id", "name"] };
			const result = await execute({ userId, params });
			setScreenerConditions(result?.data || []);
		};
		getData(userId);
	}, [userId, execute]);

	return (
		<div className="flex gap-0.5 p-1 items-center">
			<Popover>
				<PopoverTrigger asChild>
					<Button variant="outline" className="h-[18px]">
						Load
					</Button>
				</PopoverTrigger>
				<PopoverContent
					className="flex flex-col w-56 overflow-y-scroll h-full
						min-h-20 max-h-40 ml-3 px-2 py-1.5 border !border-border-light"
				>
					{screenerConditions.length !== 0 ? (
						screenerConditions.map((screenerCondition) => (
							<span
								key={screenerCondition.name}
								className="max-w-56 h-full justify-between bg-[var(--dark-background)]
									text-stone-400 hover:text-accent-foreground text-start my-0.5 px-2 py-1 gap-2"
								onClick={() => setSelectedBacktest(screenerCondition)}
								onKeyDown={(event) => {
									if (event.key === KEY.ENTER || event.key === KEY.SPACE) {
										event.preventDefault();
										setSelectedBacktest(screenerCondition);
									}
								}}
							>
								<p className="whitespace-normal break-all">
									{screenerCondition.name}
								</p>
							</span>
						))
					) : (
						<h2 className="flex self-center text-accent-foreground">
							{isLoading ? "Loading..." : "No states"}
						</h2>
					)}
				</PopoverContent>
			</Popover>

			<p className="h-max py-1 pl-1">
				{selectedBacktest ? selectedBacktest.name : "Backtest not selected"}
			</p>
		</div>
	);
};
